import Image from 'next/image'
import { IconButton, Nav, Text } from 'rsuite'
import BrandLogo from '@/components/shared/brandLogo'
import SkipToContent from '@/components/shared/skipToContent'
import { TbWorld, TbSearch } from 'react-icons/tb'
import { useRouter } from 'next/router'
import useTranslation from '@/hooks/useTranslation'
import style from './desktopHeader.module.scss'
import { getHeaderTitle, getLanguageLabel } from '@/utils/headerHelper'
import { useSelector } from 'react-redux'
import { configSelectors } from '@/store/configs'
import { headerSelectors } from '@/store/header'
import cn from 'classnames'
import SearchModal from './searchModal'
import { useState } from 'react'
import ChangeVehicleIcon from '@/components/themes/common/icons/changeVehicleIcon'
import { customerHelper } from '@tweddle-se-team/common/constants'

const { CUSTOMER_IDS } = customerHelper

const authenticatedOptionsPlacementByCustomer = {
  [CUSTOMER_IDS.SUBARU]: { header: false, ribbon: true },
  [CUSTOMER_IDS.NORDICS]: { header: true, ribbon: false }
}

function DesktopHeader({
  returnToLandingPage = () => {},
  autentincatedOptions,
  isOnline,
  customerId,
  locale,
  setShowLanguageSelector,
  resetVehicle,
  canSwitchLanguage,
  showLogoHeader = true
}) {
  const t = useTranslation()
  const router = useRouter()
  const { brand, model, modelType, year, image } = useSelector(headerSelectors.selectModel)
  const languages = useSelector(configSelectors.selectConfigByKey('languages'))
  const title = getHeaderTitle({ year, model, modelType, customerId, t })
  const languageLabel = getLanguageLabel({ languages, locale, t })
  const [showSearchModal, setShowSearchModal] = useState(false)
  const isPdfPage = router.pathname.startsWith('/pdf/')
  const enableSearch = brand && model && modelType && year && isOnline
  const showRibbonButtons = !!model || (!isPdfPage && canSwitchLanguage)
  const authenticatedOptionsPlacement =
    authenticatedOptionsPlacementByCustomer[customerId] || authenticatedOptionsPlacementByCustomer[CUSTOMER_IDS.SUBARU]

  return (
    <div className={style.desktopHeader}>
      <SkipToContent />
      {showLogoHeader && (
        <div className={style.headerColumn}>
          <BrandLogo brand="subaru" onClick={e => returnToLandingPage(e, isOnline)} height={40} width={150} />
          {authenticatedOptionsPlacement.header && <Nav className={style.headerActions}>{autentincatedOptions}</Nav>}
        </div>
      )}
      <div className={`${style.ribbon} dark-section`}>
        <div className={style.ribbonContent}>
          {image && (
            <div className={style.modelImageWrapper}>
              <Image
                src={image}
                alt="Model"
                width={120}
                height={60}
                style={{ objectFit: 'contain', borderRadius: 8, background: '#fff' }}
              />
            </div>
          )}
          <div className={cn(style.ribbonMainColumn, { [style.ribbonMainColumnCentered]: !showRibbonButtons })}>
            {showRibbonButtons && (
              <div className={style.ribbonButtons}>
                {!!model && (
                  <IconButton
                    id="btn-changeVehicle"
                    className={style.vehicleBtn}
                    icon={<ChangeVehicleIcon size={16} />}
                    size="xs"
                    appearance="ghost"
                    onClick={() => {
                      if (resetVehicle) resetVehicle()
                      router.push('/')
                    }}
                  >
                    {t('header_change_vehicle', 'Change Vehicle')}
                  </IconButton>
                )}
                {!isPdfPage && canSwitchLanguage && (
                  <IconButton
                    id="btn-language"
                    className={style.languageBtn}
                    icon={<TbWorld size={16} />}
                    size="xs"
                    appearance="ghost"
                    onClick={() => setShowLanguageSelector(true)}
                  >
                    {languageLabel}
                  </IconButton>
                )}
              </div>
            )}
            <Text as="h1" id="navbar-title" className={style.ribbonTitle}>
              {title}
            </Text>
          </div>
        </div>
        <div className={cn('flex-r', style.ribbonNav)}>
          <Nav>
            {enableSearch && (
              <Nav.Item onClick={() => setShowSearchModal(!showSearchModal)}>
                <span className={cn('flex-l', style.searchLabel)}>
                  <TbSearch className="mr-10" />
                  {t('header_search', 'Search')}
                </span>
              </Nav.Item>
            )}
            {authenticatedOptionsPlacement.ribbon && autentincatedOptions}
          </Nav>
        </div>
      </div>
      {enableSearch && (
        <SearchModal
          open={showSearchModal}
          onClose={() => setShowSearchModal(false)}
          brand={brand}
          model={model}
          modelType={modelType}
          year={year}
          language={locale}
        />
      )}
    </div>
  )
}

export default DesktopHeader
